/**
 * Layout Context
 *
 * Provides dynamic layout state for components that need to signal layout modes
 * (like split-view) that affect global elements such as the footer.
 *
 * Background:
 * Some pages use a split-view layout (table + sidebar) that requires adjusted
 * footer styles (reduced padding). For route-level pages, this is configured via
 * the route handle (`layout: 'split-view'` in Router.tsx). However, for components
 * that appear as steps within a multi-step form (not as standalone routes), we need
 * a way to dynamically signal the layout mode.
 *
 * Example use cases:
 * - SSUM Create User Form → Properties Assignment Step
 *   (features/SSUM/CreateUser/Form/Steps/PropertiesAssignmentStep.tsx)
 * - SSUM User Details → Properties Assignment page uses route-level config
 *   (routes.userPropertiesAssignment with `layout: 'split-view'`)
 *
 * @see Router.tsx for route-level layout configuration
 * @see PageWrapper/V2/index.tsx for how route and context layouts are combined
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  type ReactNode,
  type FC,
} from 'react';

type LayoutContextType = {
  /**
   * Whether a component has requested split-view layout mode.
   * This is used to adjust footer styles for split-pane layouts.
   */
  isSplitView: boolean;
  /**
   * Set the split-view layout mode.
   */
  setIsSplitView: (value: boolean) => void;
};

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

type LayoutProviderProps = {
  children: ReactNode;
};

/**
 * Provider for dynamic layout state that components can use to signal
 * layout modes (like split-view) that affect global elements like the footer.
 */
export const LayoutProvider: FC<LayoutProviderProps> = ({ children }) => {
  const [isSplitView, setIsSplitView] = useState(false);

  const value: LayoutContextType = useMemo(() => ({ isSplitView, setIsSplitView }), [isSplitView]);

  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

/**
 * Hook to access the layout context.
 * Must be used within a LayoutProvider.
 */
export const useLayoutContext = (): LayoutContextType => {
  const context = useContext(LayoutContext);

  if (!context) {
    throw new Error('useLayoutContext must be used within a LayoutProvider');
  }

  return context;
};

/**
 * Hook for components that need split-view footer styles.
 * Automatically enables split-view mode on mount and disables on unmount.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   useSplitViewLayout();
 *   return <div>Split view content</div>;
 * };
 * ```
 */
export const useSplitViewLayout = (): void => {
  const { setIsSplitView } = useLayoutContext();

  useEffect(() => {
    setIsSplitView(true);
    return () => setIsSplitView(false);
  }, [setIsSplitView]);
};
